// Shortcut prefix for Masters menu items
export const MASTER_PREFIX = 'Master';

/**
 * Cycle stage values.
 * These map to the `stage` field on project/cycle documents.
 */
export const CYCLE_STAGES = {
  OPEN: 0,
  PENDING_REVIEW: 1,
  CLOSED: 3,
};

/**
 * Labels for cycle stage values (used in UI display).
 */
export const STAGE_LABELS = {
  [0]: 'Open',
  [1]: 'Pending for review',
  [3]: 'Closed',
};

/**
 * Financial Close route path helpers and prefixes.
 */
export const FC_ROUTES = {
  HOME: '/financialclose/home',
  HOMEVIEW: '/financialclose/homeview',
  SUPPORT: '/financialclose/support',
  PROJECT_PREFIX: '/financialclose/project/',
  CHECKLIST: (id) => `/financialclose/project/checklist/${id}`,
  DASHBOARD: (id) => `/financialclose/project/dashboard/${id}`,
};

/**
 * Regex to detect undefined project IDs in financial close routes.
 * Matches any /financialclose/…/undefined path segment, covering nested
 * routes such as /financialclose/automation/riskmanagementhome/:id,
 * /financialclose/manage/financialstatementpreparation/:id, etc.
 */
export const FC_UNDEFINED_ID_PATTERN = /\/financialclose\/(?:[^/]+\/)+undefined(?:\?|$|\/)/;

/**
 * Menu item name constants used across FC components.
 */
export const FC_MENU_NAMES = {
  MASTERS: 'Masters',
  SHORTCUTS: 'Shortcuts',
  CYCLES: 'Cycles',
  BOLT_ON_MODULES: 'Bolt On Modules',
  CYCLE_SETTINGS: 'Cycle Settings',
  TASKLIST: 'Tasklist',
  LOCATION: 'Location',
};

/**
 * API endpoints for the financialclose project store.
 */
export const FC_PROJECT_API = {
  GET: '/v2/financialclose/project/get',
  GET_V3: '/v3/financialclose/analysis/getprojects',
  GET_FORM_DATA: '/v2/financialclose/project/getformdata/',
  ADD: '/v2/financialclose/project/add',
  EDIT: (id) => `/v2/financialclose/project/edit/${id}`,
  DELETE: (id) => `/v2/financialclose/project/delete/${id}`,
  CLOSE: (id) => `/v2/financialclose/project/close/${id}`,
  OPEN: (id) => `/v2/financialclose/project/open/${id}`,
  DISABLE_NOTIFICATION: (id) => `/v2/financialclose/project/disablenotification/${id}`,
  ENABLE_NOTIFICATION: (id) => `/v2/financialclose/project/enablenotification/${id}`,
  GENERATE_AI_CHECKLIST: '/v2/financialclose/project/generateaichecklist',
  RETRY_CYCLE_CREATION: (id) => `/v2/financialclose/project/retrycyclecreation/${id}`,
  GET_DASHBOARD: (id) => `/v2/financialclose/analysis/getdashboard/${id}`,
  CHART_OF_ACCOUNTS_GET: '/v2/financialclose/masters/chartofaccount/get',
  ERP_SYNC_TRIAL_BALANCE: 'v2/erp/sync-trialbalance',
};

/**
 * Cycle creation status values for BookClosure projects.
 */
export const CYCLE_CREATION_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  PARTIALLY_COMPLETED: 'partially_completed',
  FAILED: 'failed',
};

/**
 * Generates the activity log API URL for a given resource ID.
 * @param {string} id - The resource _id
 * @returns {string} The full activity log API endpoint URL
 */
export const activityLogUrl = (id) => `/v2/activitylogs/get/${id}`;

export const CYCLE_MESSAGES = {
  SUCCESS: {
    NEW_CYCLE_CREATED: "New cycle created",
    PROJECT_UPDATED: "Project Updated",
    PROJECT_CLOSED: "Project Closed",
    PROJECT_REOPENED: "Project Re-opened",
    EMAIL_NOTIFICATION_DISABLED: "Email Notifications Disabled",
    EMAIL_NOTIFICATION_ENABLED: "Email Notifications Enabled",
    EMAIL_NOTIFICATION_SUCCESS: type => `${type} successfully`,
    STATUS_CHANGED: 'Status changed',
    CYCLE_CREATION_RETRY: 'Cycle setup retry initiated',
    CYCLE_DELETED: 'Cycle deleted successfully',
  },
  ERROR: {
    UNKNOWN: "Unknown error!",
    SELECT_POPULATE_CHECKLIST: "Select Populate checklist",
    ERROR_CLOSING_PROJECT: "Error closing project",
    ERROR_REOPENING_PROJECT: "Error re-opening project",
    ERROR_GETTING_USER_INFO: "Error getting the user information",
    PLEASE_SELECT_START_DATE: "Please select start date",
    PLEASE_SELECT_END_DATE: "Please select end date",
    PLEASE_ENTER_DESCRIPTION: "Please enter description",
    PLEASE_SELECT_CYCLE_TYPE: "Please select a cycle type",
    PLEASE_SELECT_CYCLE_LOCATIONS: "Please select at least one location",
    NO_TAGS_SELECTED: "Please select at least one tag",
    NO_PROJECT_SELECTED: "Please select a project",
    UNSUPPORTED_FILE_EXTENSION: "Only xlsx,xls,xlsm,xlsb file extension supported",
    ERROR_CHANGING_STAGE: 'Error changing stage',
  },
  PROJECT: {
    CHECKLIST_MASTER: 'checklistmaster',
  }
};

// Status and change status text for InformationManager.vue
export const STATUS_TEXT = {
  's-1': { text: 'Cancelled', color: 'error' },
  's0': { text: 'Pending Submission', color: 'info' },
  's1': { text: 'Pending Approval', color: 'warning' },
  's2': { text: 'Approved', color: 'success' }
};

export const CHANGE_STATUS_TEXT = {
  'pending': { text: 'Pending', stages: [2, -1], changeto: 0, color: 'info' },
  'approve': { text: 'Approve', stages: [1], changeto: 2, color: 'success' },
  'close': { text: 'Close', stages: [0], changeto: 2, color: 'success' },
  'cancel': { text: 'Cancel', stages: [0, 1, 2], changeto: -1, color: 'error' },
};

/** Maximum number of days from today allowed for due date selection */
export const MAX_DUE_DATE_DAYS = 200;

/** Activity log event types used by activity-log component. */
export const ACTIVITY_LOG_TYPES = {
  TASKLIST: ["cycle_task_created","cycle_task_deleted","cycle_task_bulk_created","cycle_bulk_task_updated", "project_create", "projectcreate", "project_update", "projectupdate"],
  TRIAL_BALANCE: ['cycle_tb_add'],
  TASKLIST_IM: ["cycle_bulk_create_information_manager","cycle_information_manager_added","cycle_information_manager_deleted"],
  USERS: ['userassociate', 'usermoved', 'userreplaced', 'userremoved'],
  MASTER_CHART_OF_ACCOUNT: ["master_chart_of_account_add","master_chart_of_account_bulkadd"],
  MASTER_TASKLIST: ["master_task_created","master_bulk_task_created","master_bulk_task_updated","master_add_custom_column"],
  MASTER_IM:["master_create_information_manager","master_bulk_create_information_manager"]
};

export const REQUEST_TYPES = {
  text: 'Text',
  number: 'Number',
  fileupload: 'File Upload',
  link: 'URL Link',
  fileuploadtolink: 'File Upload Link',
  multiselect: 'Multi Select',
  choice: 'Choice',
};

export const INFORMATION_MANAGER_STAGE_LABEL_MAP = {
  '-1': 'Cancelled',
  '0': 'Pending Submission',
  '1': 'Pending Approval',
  '2': 'Approved',
};

/**
 * Stage values used for filtering requests by status in summary cards
 * Maps to the `stage` field in request line items
 * - null: Show all requests (no filter)
 * - 0: Pending Submission
 * - 1: Pending Approval
 * - 2: Approved
 * - -1: Cancelled
 */
export const INFORMATION_MANAGER_STAGE_VALUES = {
  ALL: null,
  PENDING_SUBMISSION: 0,
  PENDING_APPROVAL: 1,
  APPROVED: 2,
  CANCELLED: -1,
};

export const INFORMATION_MANAGER_TABLE_HEADERS = (stageLabelMap = {}) => ([
  {
    text: 'S No.',
    value: 'index',
    visible: true,
    filterable: false,
    sortable: false,
  },
  {
    text: 'Requested Information',
    value: 'description',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Type',
    value: 'type',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Ref',
    value: 'ref',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Classification',
    value: 'category',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Status',
    value: 'stage',
    visible: true,
    filterable: true,
    sortable: false,
    filterLabelMap: stageLabelMap,
  },
  {
    text: 'Due Days',
    value: 'duedate_days',
    visible: true,
    filterable: true,
    sortable: false,
    type: 'number-range',
  },
  {
    text: 'Requested From',
    value: 'info_owner_name',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Requested By',
    value: 'created_by_name',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Location',
    value: 'location',
    visible: true,
    filterable: true,
    sortable: false,
  },
  {
    text: 'Priority',
    value: 'priority',
    visible: true,
    filterable: true,
    sortable: true,
  },
  {
    text: 'Created On',
    value: 'created_at',
    visible: true,
    filterable: true,
    sortable: true,
    type: 'date-range',
  },
  {
    text: 'updated On',
    value: 'updated_at',
    visible: false,
    filterable: false,
    sortable: false,
  },
  {
    text: 'Action',
    value: 'action',
    visible: true,
    filterable: false,
    sortable: false,
  },
]);

export const INFORMATION_MANAGER_PRIORITY_LIST = ['High', 'Medium', 'Low'];

export const INFORMATION_MANAGER_TYPE_OPTIONS = [
  { name: 'Text', value: 'text' },
  { name: 'Fileupload', value: 'fileupload' },
];

/**
 * Constants used throughout the Financial Close module
 */


// Email notification constants
export const EMAIL_NOTIFICATION = {
    FEATURE_TYPES: {
        DASHBOARD: "dashboard",
        TASK_OVERDUE: "taskOverDueNotification",
        OVERDUE: "overdue",
        DUETODAY: "dueToday"
    },
    TYPE: "fbc",
    MAX_EMAIL_INSTANCES: 10,
    REJECTED: "rejected",
    MESSAGES: {
        SCHEDULE_UPDATE_NOTICE: "Please Note: Changes to the notification schedule might take effect after the current scheduled email is sent.",
        MAX_NOTIFICATIONS_ERROR: "Current cycle already have maximum notifications. Cannot add more",
        NOTIFICATION_NOT_ENABLED: "Notification is not enabled. Please contact the Administrator.",
        SUCCESS_CREATE: "Notification created successfully!",
        SUCCESS_UPDATE: "Notification updated successfully!",
        SUCCESS_STATUS_CHANGE: "Notification status changed successfully!",
        FAILED_GET:"Failed to fetch the required details. Please refresh or try again.",
        REQUIRED_VALIDATION: "Please fill all the required fields",
        FAILED_UPDATE:"Failed to update notification status",
        FEATURE_VALIDATION_MESSAGES:{
            FEATURE_REQUIRED: "Feature is required",
            ROLES_REQUIRED: "Role is required",
            LOCATIONS_REQUIRED: "At least one location must be selected",
            SCHEDULER_TIMING_REQUIRED: "Scheduler Timing is required"
        }
    },
    roles: [
        {
            name: "Preparer",
            value: "preparer"
        },
        {
            name: "Approver",
            value: "approver"
        },
        {
            name: "Both",
            value: "both"
        },
    ],
    timeList: Array.from({ length: 23 }, (_, i) => ({
        name: `${i + 1} Hour${i + 1 > 1 ? 's' : ''}`,
        value: (i + 1).toString()
    })),
    STATUS:{
        ENABLED: "enable",
        DISABLED: "disable"
    },
    ALL_ROLES:{
        PREPARER: "preparer",
        APPROVER: "approver",
        BOTH: "both"
    }
};

export const NOTIFICATION_DEFAULTS = {
  TASK_LIST: {
    endDays: "",
    hour: "",
    category: [],
    emails: null,
    subject: null,
    body: "",
    location: [],
    sla: null,
    priority: null,
  },
  DASHBOARD: {
    endDays: "",
    hour: "",
    emails: null,
    subject: null,
    body: "",
    location: [],
  },
  DUE_TODAY: {
    scheduler: null,
    status: false,
    location: [],
    role: [],
  },
  OVERDUE_TASK: {
    scheduler: null,
    status: false,
    location: [],
    role: [],
  },
};

// Notification type keys for comparison
export const NOTIFICATION_TYPE_KEYS = {
  OVERDUE_TASK: "overdue",
  DUE_TODAY: "dueToday",
  DASHBOARD: "dashboard",
  TASK_LIST_ESCALATION: "taskOverDueNotification",
};

export const NOTIFICATION_TYPES = [
  { name: "Tasks Overdue", value: NOTIFICATION_TYPE_KEYS.OVERDUE_TASK },
  { name: "Tasks Due Today", value: NOTIFICATION_TYPE_KEYS.DUE_TODAY },
  { name: "Dashboard", value: NOTIFICATION_TYPE_KEYS.DASHBOARD },
  {
    name: "Task list Escalation",
    value: NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION,
  },
];

// Schedule change notice message
export const SCHEDULE_CHANGE_NOTICE =
  "Please note : Changes to the notification schedule might take effect after the current schedule email is sent";

// Default cycle duration in days
export const DEFAULT_CYCLE_DURATION = 15;

// headers required for table due notifications page
export const dueNotificationHeaders = ()=> {
    let headers = [
        { value: "indexno", text: "#", sortable: false, filterable: false },
        {
          value: "labelName",
          text: "Notification Type",
          sortable: true,
          filterable: true,
        },
        {
          value: "schedulerTiming",
          text: "Scheduled At",
          sortable: true,
          filterable: true,
        },
        {
          value: "locationString",
          text: "Locations",
          sortable: true,
          filterable: true,
        },
        // {
        //   value: "roleString",
        //   text: "Roles",
        //   sortable: true,
        //   filterable: true,
        // },
        { value: "status", text: "Status", sortable: true, filterable: true },
        { value: "action", text: "Action", sortable: false, filterable: false },
      ]
      return headers;
}

// Cron time helper
export const CRON = {
  // Convert hour to cron format (minute hour * * *)
  hourToCronFormat: (hour) => {
    // If hour is "24", treat as "0" (midnight)
    const cronHour = hour === "24" ? "0" : hour;
    return `0 ${cronHour} * * *`;
  },
    // Reverse: Extract hour from cron format "0 {hour} * * *"
  cronFormatToHour: (cronStr) => {
    // Assumes format "0 {hour} * * *"
    if (typeof cronStr !== 'string') return null;
    const parts = cronStr.trim().split(' ');
    if (parts.length < 2) return null;
    let hour = parts[1];
    // If cron hour is "0", treat as "24" for UI
    if (hour === "0") hour = "24";
    return hour ?? '24';
  }
};

export const MESSAGES = {
    INACTIVE_APPROVER_ERROR: "Following approver(s) are inactive users. Please select active users only.",
    INACTIVE_PREPARER_ERROR: "Following preparer(s) are inactive users. Please select active users only.",
    FAILED_TO_GET_USERS: "Failed to fetch users.",
    SELECTED_INACTIVE_USERS:"Selected user is not active. Please remove this user"
};

// UI Text Constants
export const UI_TEXT = {
  SELECT_ALL: "Select All",
  ALL: "All",
  NA: "NA"
};

export const ACTION_STAGE = {
    ADD_APPROVERS:3,
    ADD_PREPARERS:2,
    REMOVE_USER :"remove",
    MOVE_USER :"move",
    REPLACE_USER :"replace"

}

// API endpoints
export const API_ENDPOINTS = {
  UPDATE_CREATE_SCHEDULER: "v2/financialclose/analysis/updateOrCreateSchedulerFeature",
  GET_ALL_SCHEDULER: "/v2/financialclose/analysis/getAllSchedulerFeature",
  ADD_TASK_OVERDUE_NOTIFICATION: "/v2/financialclose/analysis/addTaskOverdueNotification",
  EDIT_TASK_OVERDUE_NOTIFICATION: "/v2/financialclose/analysis/editTaskOverdueNotification",
  // GET_ALL_AVAILABLE_FEATURES_BY_TYPE:"/v2/financialclose/analysis/getAllAvailableFeatures",
  GET_PROJECT: "/v2/financialclose/project/get",

  GET_ALL_AVAILABLE_FEATURES_BY_TYPE:"/v2/financialclose/notification/getAllExistingNotifications",
  GET_NOTIFICATIONS_BY_TYPE:"/v2/financialclose/notification/getAllExistingNotifications",
  CREATE_OR_UPDATE_TASK_DUE_NOTIFICATION :"/v2/financialclose/notification/createAndUpdateOverDueAndDueTodayNotification",
  ADD_AND_UPDATE_ESCALATION_AND_DASHBOARD_NOTIFICATION: "/v2/financialclose/notification/addAndUpdateEscalationAndDashboardMasterNotification",
  CHANGE_NOTIFICATION_STATUS:"/v2/financialclose/notification/enableOrDisableNotificationFeature",

  GET_COUNTRYWISE_LOCATION:"/v2/financialclose/location/getcountrywiselocation/",
  GET_DASHBOARD_WITH_FILTERS:"/v2/financialclose/analysis/getdashboardwithfilter/",
  GET_CLIENT:"/v2/client/get/",
  GET_DASHBOARD: "/v2/financialclose/analysis/getdashboard/",
  GET_DEFAULT_DASHBOARD: "/v2/financialclose/analysis/getdefaultdashboard/",
  GET_USERS_FORMDATA: "/v2/financialclose/analysis/getusersformdata",
  GET_DASHBOARD_DETAILS: "/v2/financialclose/analysis/getdetails/",

  //  GET_NOTIFICATIONS_BY_TYPE:"/v2/financialclose/analysis/getAllNotifications",
  //  CREATE_OR_UPDATE_TASK_DUE_NOTIFICATION :"/v2/financialclose/analysis/createAndUpdateOverDueAndDueTodayFeature",
  // ADD_AND_UPDATE_ESCALATION_AND_DASHBOARD_NOTIFICATION: "/v2/financialclose/notification/addAndUpdateEscalationAndDashboardMasterNotification",
  // CHANGE_NOTIFICATION_STATUS:"/v2/financialclose/analysis/enableOrDisableNotificationFeature",

  // V3 endpoints
  V3_GET_FORMDATA: "/v3/financialclose/analysis/getformdata",
  V3_GET_DASHBOARD: "/v3/financialclose/analysis/getdashboard/",
};

export const ERROR_KEYS_CYCLE_TASKLIST = [
  "preparer", "approver", "linkedgl", "description", "duedate",
  "category", "subcategory", "priority", "location", "evidences"
];

export const ERROR_KEYS_FC_MASTER_TASKLIST = [
    "preparer", "approver", "glcode", "location", "description",
    "duedays", "category", "subcategory", "priority", "tags", "evidences"
  ];

/**
 * Header value constants for tasklist table columns.
 * Used for value-based lookups instead of hardcoded array indices.
 */
export const HEADER_VALUES = {
  TAGS: 'tags',
  NO_OF_EVIDENCE: 'no_of_evidence',
};

/**
 * Per-user-type visibility rules for specific tasklist table headers.
 * Maps user type → header value → visible boolean.
 */
export const USER_TYPE_HEADER_VISIBILITY = {
  approver: { [HEADER_VALUES.TAGS]: true, [HEADER_VALUES.NO_OF_EVIDENCE]: false },
  preparer: { [HEADER_VALUES.TAGS]: false, [HEADER_VALUES.NO_OF_EVIDENCE]: true },
  manager: { [HEADER_VALUES.TAGS]: true, [HEADER_VALUES.NO_OF_EVIDENCE]: true },
};


// Table Headers values
export function getTasklistTableHeaders(locationHeader = "Location") {
    return [
        {
            text: "Task type",
            value: "task_type",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Task description",
            value: "description",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Ref",
            value: "ref",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Status",
            value: "stage1",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Action",
            value: "upload",
            visible: true,
            filterable: false,
            sortable: false,
            type: "action",
        },
        {
            text: "Due days",
            value: "duedays",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
          text: "TB amount",
          value: "__tbtotals",
          visible: true,
          filterable: false,
          sortable: false,
          headerClass: "noTransform",
        },
        {
            text: "Recon amount",
            value: "scheduleAmount",
            visible: true,
            filterable: false,
            sortable: false,
        },
        {
            text: "Explained difference",
            value: "explainedDifference",
            visible: true,
            filterable: false,
            sortable: false,
        },
        {
            text: "Unexplained difference",
            value: "difference",
            visible: true,
            filterable: false,
            sortable: false,
        },
        {
            text: "Due date",
            value: "duedate1",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Category",
            value: "category",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Sub category",
            value: "subcategory",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
          text: "Purpose",
          value: "purpose",
          visible: false,
          filterable: true,
          sortable: true,
        },
        {
          text: "Instruction",
          value: "instruction",
          visible: false,
          filterable: true,
          sortable: true,
        },
        {
            text: locationHeader,
            value: "locationName",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Priority",
            value: "priority",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Tags",
            value: "tags",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "No. of evidences",
            value: "no_of_evidence",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Created on",
            value: "created_at",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Preparer",
            value: "preparerids",
            visible: false,
            filterable: true,
            sortable: true,
        },
        {
            text: "Approver",
            value: "approverids",
            visible: true,
            filterable: true,
            sortable: true,
        },
        {
            text: "Information request",
            value: "inforrequest",
            visible: false,
            filterable: false,
            sortable: false,
        },
        {
            text: "Submission date",
            value: "submited_on",
            visible: false,
            filterable: true,
            sortable: false,
        },
        {
            text: "Completion date",
            value: "closed_date",
            visible: false,
            filterable: true,
            sortable: false,
        },
        {
            text: "Status detail",
            value: "statusdetail",
            visible: false,
            filterable: true,
            sortable: false,
        },
        {
            text: "More action",
            value: "action",
            visible: true,
            filterable: false,
            sortable: false,
            type: "action",
            draggable: false,
            class: "more-action-column"
        },
    ];
}

// Task stage constants
export const TASK_STAGES = {
  PENDING_SUBMISSION: 0,
  PENDING_APPROVAL: 2,
  CLOSED: 3
};

export const TASK_STAGE_DISPLAY = {
  [TASK_STAGES.PENDING_SUBMISSION]: {
    class: "pending_sub_tab radius-medium px-2 py-1",
    icon: "mdi-circle-small",
    text: "Pending Submission"
  },
  [TASK_STAGES.PENDING_APPROVAL]: {
    class: "pending_app_tab radius-medium px-2 py-1",
    icon: "mdi-circle-small",
    text: "Pending Approval"
  },
  [TASK_STAGES.CLOSED]: {
    class: "closed_tab radius-medium px-2 py-1",
    icon: "mdi-circle-small",
    text: "Closed "
  }
};

// Task tabs configuration
export const TASK_TABS = [
  {
    key: null,
    label: 'Total Tasks',
    summaryKey: 'count',
    tabClass: 'total_task_tab',
    borderClass: 'total_task-border'
  },
  {
    key: 0,
    label: 'Pending Submissions',
    summaryKey: 'pending',
    tabClass: 'pending_sub_tab',
    borderClass: 'pending-task-border'
  },
  {
    key: 2,
    label: 'Pending Approvals',
    summaryKey: 'pendingapproval',
    tabClass: 'pending_app_tab',
    borderClass: 'approve-task-border'
  },
  {
    key: 3,
    label: 'Approved',
    summaryKey: 'approved',
    tabClass: 'closed_tab',
    borderClass: 'closed-task-border'
  },
  // {
  //   key: -1,
  //   label: 'Rejected',
  //   summaryKey: 'cancelled',
  //   tabClass: 'error-tab',
  //   borderClass: 'error-task-border'
  // }
];

/**
 * Maps close-cycle task stage codes to StatusIndicator variants.
 * Colors align with Tasklist chips and TaskStageTabs indicators
 * (--color-pending-sub-indicator, --color-pending-app-indicator, etc.).
 */
export const TASK_STAGE_STATUS_VARIANT = {
  0: 'pending-submission',
  1: 'fc-pending-approval',
  2: 'fc-pending-approval',
  3: 'fc-approved',
  [-1]: 'fc-rejected',
};

/** Maps task priority labels to CSS modifier keys (e.g. task-card-detail__priority--high). */
export const TASK_PRIORITY_CSS_MODIFIER = {
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

export const TASK_PRIORITY_CSS_MODIFIER_DEFAULT = 'low';

// Information Manager CSV Upload Expected Headers
export const IM_CSV_TABLE_HEADERS = [
  'Checklist Tag', 'Classification', 'Description', 'Info Owner Name', 'Info Owner Email',
  'File Upload', 'File Upload Link', 'Priority', 'Due Date (dd/mm/yyyy)', 'Location'
];

// Information Manager File Upload Types
export const IM_FILE_UPLOAD_TYPES = {
  TEXT: 'text',
  FILE_UPLOAD: 'fileupload'
};

// Error messages
export const ERROR_MESSAGES = {
  // CSV Upload errors
  NO_FILE_PROVIDED: 'No file provided',
  INVALID_FILE_EXTENSION: 'Only csv file extension supported',
  INVALID_XLSX_FILE_EXTENSION: 'Only xlsx file extension supported',
  INVALID_CSV_HEADERS: (headers) => `Columns '${headers.join(", ")}' are required in the same order`,

  // Validation errors
  DESCRIPTION_REQUIRED: 'Description is required',
  INFO_OWNER_NAME_REQUIRED: 'Info Owner Name is required',
  INFO_OWNER_EMAIL_REQUIRED: 'Info Owner Email is required',
  LOCATION_REQUIRED: 'Location is required',
  TAGS_NOT_FOUND: (tags, location) => `Tag(s) '${tags.join(", ")}' not found in checklist for location: ${location}`,

  // Custom columns errors
  FAILED_GET_CUSTOM_COLUMNS: 'Failed to get custom columns',
  FAILED_FORMAT_CUSTOM_COLUMNS: 'Failed to format custom columns',

  // Date errors
  INVALID_DATE_INPUT: 'Invalid date input',

  // Transaction Matching Reconciliation errors
  ANALYSIS_NOT_RUN: 'Analysis has not yet been run',
  ERROR_GET_COMBO_RESULT: 'Error Get Combo Result',
  ERROR_GET_COMBO_CODE: 'Error Get Combo Code',
  UNKNOWN_ERROR: 'Unknown error!',

  // Information Manager user-facing messages for store/informationManager.js
  INFORMATION_REQUIREMENTS_UPLOADED: 'Information Requirements uploaded successfully',
  ERROR_UPLOADING_INFORMATION_REQUIREMENTS: 'Error uploading Information Requirements',
  PROJECT_ID_REQUIRED: 'Project id is required',
  UNABLE_TO_LOAD_INFORMATION_MANAGER_DETAILS: 'Unable to load Information Manager details',
  NO_PROJECT_FOUND: 'No project found',
  ERROR_FETCHING_REQUEST_DETAILS: 'Error fetching request details',
  ERROR_FETCHING_INFORMATION_OWNER_LIST: 'Error fetching information owner list',
  ERROR_FETCHING_DATA: 'Error fetching data',
  NEW_REQUEST_ADDED: 'New Request Added',
  ERROR_ADDING_REQUEST: 'Error adding request',
  REQUEST_UPDATED: 'Request Updated',
  ERROR_EDITING_REQUEST: 'Error editing request',
  REQUEST_DELETED: 'Request Deleted',
  ERROR_DELETING_REQUEST: 'Error deleting request',
};

// Transaction Matching Reconciliation constants
export const TM_RECONCILIATION = {
  FILE_TYPES: {
    ONE_FILE: 'One File',
    TWO_FILE: 'Two File',
    THREE_FILE: 'Three File',
    SINGLE_FILE: 'Single File',
    MORE: 'More'
  },
  ANALYSIS_TYPES: {
    ACCOUNTS_RECEIVABLE: 'Accounts receivable Reconciliation',
    ACCOUNTS_PAYABLE: 'Accounts payable Reconciliation',
    PG_RECONCILIATION: 'PG Reconciliation (PGR)'
  },
  ANNEXURE_NAMES: {
    // Support both old (with typo) and new (correct spelling) for backward compatibility
    RECONCILIATION_SUMMARY: 'Reconciliation Summary',
    RECONCILIATION_SUMMARY_LEGACY: 'Reconcillation Summary'
  },
  SUMMARY_TYPES: {
    RESULT: 'Result',
    RECONCILIATION: 'Reconciliation'
  },
  VIEW_MODES: {
    GL: 'GL',
    SL: 'SL'
  },
  COMBO_CODE_LABELS: {
    SUMMARY: 'Summary',
    RECONCILIATION: 'Reconciliation'
  },
  COLLECTION_FILTERS: {
    ANNEXURE_SUMMARY: 'annexure_summary',
    ANNEXURE_RECON_SUMMARY: 'annexure_reconSummary'
  },
  EXCLUDED_FILTER: {
    RECONCILIATION: 'Total Excluded',
    AMOUNT: "{'LESS': [], 'ADD': []}"
  },
  NO_DATA_MESSAGE: 'No data available',
  // Group names for reconciliation data grouping
  GROUPS: {
    TABLE_1_TOTAL: 'Table 1 Total',
    EXPLAINED_DIFFERENCE: 'Explained difference',
    UNEXPLAINED_DIFFERENCE: 'Unexplained difference',
    NET_ADJUSTED_AMOUNT: 'Net Adjusted Amount',
    TABLE_2_TOTAL: 'Table 2 Total',
    UNGROUPED: 'Ungrouped'
  },
  // Order for displaying groups (Table 2 Total always last, no grouping needed)
  GROUP_DISPLAY_ORDER: [
    'Table 1 Total',
    'Explained difference',
    'Unexplained difference',
    'Ungrouped',
    'Net Adjusted Amount',
    'Table 2 Total'
  ]
};

// API endpoints for Transaction Matching Reconciliation
export const TM_RECONCILIATION_API = {
  GET_OVERALL_SUMMARY: '/v2/bot/analysis/readcombocodewisereconciliation/overallreconsummary',
  GET_COMBO_SUMMARY: '/v2/bot/analysis/readcombocodewisereconciliation/',
  GET_UNIQUE_COMBO_CODES: '/v2/bot/analysis/getalluniquecombocode/'
};

// Tags list for task remarks and approvals
export const TAGS_LIST = ["#AI"];

// Task upload modal button labels
export const TASK_UPLOAD_LABELS = {
  UPLOAD: 'Upload',
  UPLOAD_AND_SUBMIT: 'Upload & Submit'
};

// Component event names (for $emit)
export const COMPONENT_EVENTS = {
  // Common events
  REFRESH: 'refresh',
  RELOAD: 'reload',
  RESET: 'reset',
  
  // Update events (v-model related)
  UPDATE_MODEL_VALUE: 'update:modelValue',
  UPDATE_UPLOAD_WITHOUT_FILE: 'update:uploadWithoutFile',
  UPDATE_FILE_REMARK: 'update:fileremark',
  UPDATE_SELECT_GLS: 'update:selectgls',
  UPDATE_SELECTED_LOCATION: 'update:selected-location',
  UPDATE_PREPARER: 'update:preparer',
  UPDATE_APPROVER: 'update:approver',
  UPDATE_SHOW_LOCATION_AND_USER: 'update:showlocationanduser',
  UPDATE_NUMBER_OF_EVIDENCES: 'update:number-of-evidences',
  
  // Task-related events
  TASK_SUBMITTED: 'task-submitted',
  TASK_UPDATED: 'task-updated',
  TASK_INSTANT_UPDATED: 'task-instant-updated',
  CHECKLIST_ITEM_UPDATED: 'checklist-item-updated',
  
  // Upload-related events
  BULK_UPLOAD_COMPLETE: 'bulk-upload-complete',
  CLEAR_SELECTED: 'clear-selected',
  FILE_UPLOADED: 'file-uploaded',
  DELETE_FILE: 'delete-file',
  
  // Navigation/Tab events
  LINKED_GL_TAB: 'linked-gl-tab',
  BACK: 'back',
  
  // User/Location events
  LOCATION_USERS_SUBMIT: 'location-users-submit',
  USER_TYPE_CHANGE: 'user-type-change',
  
  // Status change events
  CHANGE_STATUS: 'change-status',
  
  // Dialog/Modal events
  CLOSE_POPUP: 'close-popup',
  ADD_EDIT_CHECKLIST_ITEM: 'add-edit-checklist-item',
  OPEN_UPLOAD_DRAWER: 'open-upload-drawer',
  OPEN_RECONCILIATION_DIALOGUE: 'open-reconciliation-dialogue',
  
  // Info request events
  APPROVE: 'approve',
  REJECT: 'reject',
  FOLLOWUP: 'followup',
  
  // Filter events
  FILTER_CHANGED: 'filter-changed',
  TOGGLE: 'toggle',
  EDIT: 'edit',
  
  // Loading events
  START_LOADING: 'startloading',
  STOP_LOADING: 'stoploading',
  
  // Data events
  STORED_DATA: 'storedData',
  INPUT: 'input',
  
  // Download events
  DOWNLOAD_RESULT: 'download-result'
};

// Legacy task type labels for backward compatibility
export const LEGACY_TASK_TYPE_LABEL_CHECKLIST = 'Checklist';
export const LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME = 'Normal Task';

// Responsive table height configuration
export const TABLE_OFFSET_SMALL_SCREEN = 415; // Offset for screens < 1080px height
export const TABLE_OFFSET_LARGE_SCREEN = 280; // Offset for screens >= 1080px height
export const LARGE_SCREEN_HEIGHT_THRESHOLD = 1080; // Height threshold to switch offset
export const MS_PER_HOUR = 1000 * 60 * 60;
export const MS_PER_DAY = MS_PER_HOUR * 24;

export const TM_API_ENDPOINTS = {
  GENERATE_RESULT: '/v3/bot/analysis/downloadfile',
  GET_RECENT_DOWNLOADS: '/v2/bot/analysis/getrecentdownloads',
  GET_TM_ANALYSIS_DETAILS: '/v2/bot/analysis/getTransactionMatchingAnalysis',
  TWO_FILES_BULK_MANUAL_MATCH: '/v3/bot/analysis/two-files-bulk-manual-match-consolidated/',
  GET_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/get',
  ADD_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/add',
  UPDATE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/update',
  DELETE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/delete',
  ENABLE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/enable',
  DISABLE_TM_MIDDLEWARE: '/v2/bot/analysis/middleware/disable',
  GET_TM_FILTER: '/v2/bot/analysis/tmfilter/get',
  UPSERT_TM_FILTER: '/v2/bot/analysis/tmfilter/upsert',
  DELETE_TM_FILTER: '/v2/bot/analysis/tmfilter/delete',
  ENABLE_TM_FILTER: '/v2/bot/analysis/tmfilter/enable',
  DISABLE_TM_FILTER: '/v2/bot/analysis/tmfilter/disable',
  REORDER_TM_FILTER: '/v2/bot/analysis/tmfilter/reorder',
  GET_INPUT_PARAMETER: '/v2/bot/analysis/inputparameter/get',
  GET_BOT_DETAILS: '/v2/bot/analysis/getbotdetails',
  LOAD_BOT_DETAILS: '/v2/bot/analysis/loadbotdetails',
  ADD_TM_ANALYSIS: '/v2/bot/analysis/addTransactionMatchingAnalysis',
  IMPORT_TM_ANALYSIS: '/v2/bot/analysis/importTransactionMatchingAnalysis',
  RESYNC_TM_ANALYSIS: '/v2/bot/analysis/resyncTransactionMatchingAnalysis',
  DELETE_TM_ANALYSIS: '/v2/bot/analysis/deleteTransactionMatchingAnalysis',
  UPDATE_TM_ANALYSIS: '/v2/bot/analysis/updateTransactionMatchingAnalysis',
  LOAD_TM_ANALYSIS: '/v2/bot/analysis/loadTransactionMatchingAnalysis',
  GET_BOT_ID_BY_NAME: '/v2/bot/analysis/getbotidbyname',
  GET_FORM_DATA: '/v2/bot/analysis/tm/getformdata',
  GET_PENDING_PAYOUTS: '/v3/bot/analysis/pending-payouts',
};

export const TM_MESSAGES = {
  FILE_GENERATING: "File is being generated please try after sometime",
  ERROR_GENERATING: "There was an error while generating file",
  NO_FILE_FOUND: "No file found to download",
  UNKNOWN_ERROR: "Unknown error!",
  DOWNLOAD_IN_PROGRESS: "A download is already in progress. Please wait for it to complete",
  EXPORT_LIMIT_EXCEEDED: (limit) => `Record count exceeds the export limit of ${limit.toLocaleString()} records`,
  EXPORT_LIMIT_EXCEEDED_GENERIC: "Record count exceeds the export limit",
  BULK_MANUAL_MATCH_SUCCESS: "Manual matching bulk upload successfully saved",
  NO_FILE_SELECTED: "No file selected or wrong file selected",
};

export const UPLOAD_TYPES = {
    NEW_FILE: 'New file',
    // ADD_TO_EXISTING: 'Add to existing',
    // REPLACE_EXISTING: 'Replace existing',
    REPLACE_OR_ADD: 'Replace or add to existing',
    DELETE_FILE: 'Delete file',
};

/** Options shown when no file exists yet for a given file type */
export const UPLOAD_TYPE_OPTIONS_NEW = [UPLOAD_TYPES.NEW_FILE];

/** Options shown when one or more files already exist for a given file type */
export const UPLOAD_TYPE_OPTIONS_EXISTING = [
    // UPLOAD_TYPES.ADD_TO_EXISTING,
    // UPLOAD_TYPES.REPLACE_EXISTING,
    UPLOAD_TYPES.REPLACE_OR_ADD,
];

export const FILE_STAGES = {
    SUCCESSFUL: 1,
    QUEUED: 16,
    PROCESSING: 17,
    FAILED: -1,
    DELETED: -2,
};

export const DOWNLOAD_STAGES = {
    QUEUED: 0,
    READY: 1,
    FAILED: 2,
    PROCESSING: 4,
    FILE_QUEUED: 16,
    FILE_PROCESSING: 17,
};

export const isDownloadInProgress = (stage) =>
    stage === DOWNLOAD_STAGES.QUEUED || stage === DOWNLOAD_STAGES.PROCESSING ||
    stage === DOWNLOAD_STAGES.FILE_QUEUED || stage === DOWNLOAD_STAGES.FILE_PROCESSING;

export const isDownloadFailed = (stage) => stage === DOWNLOAD_STAGES.FAILED;

export const isDownloadReady = (stage) => !isDownloadInProgress(stage) && !isDownloadFailed(stage);

export const TM_STATUS = {
    NOT_STARTED: 0,
    FINISHED: 1,
    FAILED: 2,
    IN_PROGRESS: 4,
    QUEUED: 5,
    AI_IN_PROGRESS: 9,
    MANUAL_MATCHING_IN_PROGRESS: 12,
    MANUAL_MATCHING_PROGRESS: 13,
};

export const TM_ACTION_STAGES = {
    QUEUED: 1,
    IN_PROGRESS: 2,
    COMPLETED: 3,
    FAILED: 4,
};

/** Action types whose queue status can override run status in TM config UI */
export const TM_STATUS_TRACKED_ACTIONS = [
    'import_analysis_data',
    'resync_analysis_data',
];

